<template>
  <div class="container">
    <!-- 面包屑导航 -->
    <el-breadcrumb separator="/">
      <el-breadcrumb-item :to="{ path: '/welcome' }">首页</el-breadcrumb-item>
      <el-breadcrumb-item>财务管理</el-breadcrumb-item>
      <el-breadcrumb-item>招商数据</el-breadcrumb-item>
    </el-breadcrumb>

    <!-- 主卡：三 Tab（收款流水 / 签约统计 / 人员绩效） -->
    <el-card class="main-card">
      <div slot="header" class="toolbar">
        <span class="card-header">
          招商数据
          <span class="header-summary">招商收款流水 · 项目签约统计 · 人员绩效汇总（财务视角，只读招商数据）</span>
        </span>
        <span class="toolbar-tip">流水默认查当月 · 导出所见即所得（跟随筛选）</span>
      </div>

      <el-tabs v-model="activeTab">
        <!-- Tab1 收款流水 -->
        <el-tab-pane label="收款流水" name="bill">
          <!-- 指标卡：6 张（随筛选联动） -->
          <div class="stat-cards">
            <div class="stat-card">
              <div class="stat-card-num">{{ bill.stats.incomeCount }}</div>
              <div class="stat-card-label">收入笔数</div>
            </div>
            <div class="stat-card">
              <div class="stat-card-num">{{ fmtMoney(bill.stats.incomeAmount) }}</div>
              <div class="stat-card-label">收入金额（元）</div>
            </div>
            <div class="stat-card">
              <div class="stat-card-num">{{ bill.stats.refundCount }}</div>
              <div class="stat-card-label">退款笔数</div>
            </div>
            <div class="stat-card">
              <div class="stat-card-num">{{ fmtMoney(bill.stats.refundAmount) }}</div>
              <div class="stat-card-label">退款金额（元）</div>
            </div>
            <div class="stat-card">
              <div class="stat-card-num">{{ fmtMoney(bill.stats.incomeAmount - bill.stats.refundAmount) }}</div>
              <div class="stat-card-label">净收款（元）</div>
            </div>
            <div class="stat-card" :class="{ warn: bill.stats.pendingCount > 0 }">
              <div class="stat-card-num">{{ bill.stats.pendingCount }}</div>
              <div class="stat-card-label">待审核笔数</div>
            </div>
          </div>

          <!-- 筛选行 -->
          <div class="filter-row">
            <el-date-picker
              v-model="bill.dateRange"
              type="daterange"
              value-format="yyyy-MM-dd"
              range-separator="至"
              start-placeholder="收款开始日期"
              end-placeholder="收款结束日期"
              size="small"
              style="width: 250px;"
              :clearable="false"
              @change="onBillFilterChange"
            ></el-date-picker>
            <el-input v-model="bill.keyword" placeholder="客户/项目" clearable size="small" style="width: 130px;" @keyup.enter.native="onBillFilterChange" @clear="onBillFilterChange"></el-input>
            <el-select v-model="bill.payType" placeholder="支付方式" clearable size="small" style="width: 110px;" @change="onBillFilterChange">
              <el-option v-for="item in payTypeList" :key="item" :label="item" :value="item"></el-option>
            </el-select>
            <el-select v-model="bill.feeType" placeholder="流量渠道" clearable size="small" style="width: 110px;" @change="onBillFilterChange">
              <el-option v-for="item in feeTypeList" :key="item" :label="item" :value="item"></el-option>
            </el-select>
            <el-select v-model="bill.payAccount" placeholder="收款账户" clearable size="small" style="width: 160px;" @change="onBillFilterChange">
              <el-option v-for="item in payAccountList" :key="item" :label="item" :value="item"></el-option>
            </el-select>
            <el-select v-model="bill.status" placeholder="审核状态" clearable size="small" style="width: 100px;" @change="onBillFilterChange">
              <el-option label="未审核" :value="0"></el-option>
              <el-option label="已审核" :value="1"></el-option>
            </el-select>
            <el-select v-model="bill.signerId" placeholder="签约人" clearable size="small" style="width: 100px;" @change="onBillFilterChange">
              <el-option v-for="item in signers" :key="item._id" :label="item.name" :value="item._id"></el-option>
            </el-select>
            <el-button size="small" @click="resetBillFilter">重置</el-button>
            <el-button size="small" icon="el-icon-download" @click="handleBillExport">导出</el-button>
          </div>

          <el-table v-loading="bill.loading" :data="bill.list" border stripe size="small" max-height="520">
            <el-table-column label="#" type="index" width="45" :index="i => (bill.pagenum - 1) * bill.pagesize + i + 1"></el-table-column>
            <el-table-column label="收款日期" width="100">
              <template slot-scope="scope">
                <div class="project-name">{{ fmtDate(scope.row.payDate) }}</div>
              </template>
            </el-table-column>
            <el-table-column label="客户" prop="customerName" min-width="100" show-overflow-tooltip></el-table-column>
            <el-table-column label="项目" min-width="140" show-overflow-tooltip>
              <template slot-scope="scope">
                <span v-if="scope.row.projectName">{{ scope.row.projectName }}</span>
                <span v-else class="unassigned-text">无项目关联</span>
              </template>
            </el-table-column>
            <el-table-column label="支付方式" prop="payType" width="85" align="center"></el-table-column>
            <el-table-column label="流量渠道" prop="feeType" width="90" align="center" show-overflow-tooltip></el-table-column>
            <el-table-column label="金额（元）" width="110" align="right">
              <template slot-scope="scope">
                <span :class="scope.row.type === 1 ? 'money-text' : 'money-refund'">{{ fmtMoney(scope.row.totalAmount) }}</span>
              </template>
            </el-table-column>
            <el-table-column label="方向" width="70" align="center">
              <template slot-scope="scope">
                <el-tag :type="scope.row.type === 1 ? 'success' : 'danger'" size="mini">{{ scope.row.type === 1 ? '收入' : '退款' }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="收款账户" prop="payAccount" min-width="140" show-overflow-tooltip></el-table-column>
            <el-table-column label="签约人" width="80" align="center">
              <template slot-scope="scope">{{ scope.row.signerName || '—' }}</template>
            </el-table-column>
            <el-table-column label="收款人" prop="receiverName" width="80" align="center"></el-table-column>
            <el-table-column label="审核状态" width="85" align="center">
              <template slot-scope="scope">
                <el-tag :type="scope.row.status === 1 ? 'success' : 'warning'" size="mini">{{ scope.row.status === 1 ? '已审核' : '未审核' }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="附件" width="120">
              <template slot-scope="scope">
                <div v-if="scope.row.attachments && scope.row.attachments.length > 0" class="att-cell">
                  <div v-for="(file, index) in scope.row.attachments" :key="index" class="att-item-btn" @click="handlePreviewAttachment(scope.row, file)">
                    <img v-if="file.type === 'image'" :src="getFileUrl(file.url)" alt="附件" class="att-thumb-sm" />
                    <img v-else-if="file.type === 'pdf'" :src="pdfIconUrl" alt="PDF" class="att-thumb-sm is-pdf" />
                    <i v-else class="el-icon-document att-doc-icon"></i>
                  </div>
                </div>
                <div v-else class="muted-text">—</div>
              </template>
            </el-table-column>
            <el-table-column label="备注" prop="remark" min-width="130" show-overflow-tooltip>
              <template slot-scope="scope">
                <span v-if="scope.row.remark">{{ scope.row.remark }}</span>
                <span v-else class="muted-text">—</span>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="110" align="center" fixed="right">
              <template slot-scope="scope">
                <el-button type="text" size="mini" @click="toggleAudit(scope.row)">{{ scope.row.status === 1 ? '取消审核' : '审核' }}</el-button>
                <el-button v-if="scope.row.payType === '退款'" type="text" size="mini" @click="openAttDialog(scope.row)">附件</el-button>
              </template>
            </el-table-column>
          </el-table>
          <el-pagination
            class="pager"
            @size-change="s => { bill.pagesize = s; bill.pagenum = 1; fetchBill() }"
            @current-change="p => { bill.pagenum = p; fetchBill() }"
            :current-page="bill.pagenum"
            :page-sizes="[10, 20, 50]"
            :page-size="bill.pagesize"
            layout="total, sizes, prev, pager, next, jumper"
            :total="bill.total">
          </el-pagination>
        </el-tab-pane>

        <!-- Tab2 签约统计 -->
        <el-tab-pane label="签约统计" name="project">
          <!-- 指标卡：4 张 -->
          <div class="stat-cards">
            <div class="stat-card">
              <div class="stat-card-num">{{ project.stats.total }}</div>
              <div class="stat-card-label">项目总数</div>
            </div>
            <div class="stat-card">
              <div class="stat-card-num">{{ project.stats.building }}</div>
              <div class="stat-card-label">筹建中</div>
            </div>
            <div class="stat-card">
              <div class="stat-card-num">{{ project.stats.opening }}</div>
              <div class="stat-card-label">开业中</div>
            </div>
            <div class="stat-card" :class="{ warn: project.stats.rescinded > 0 }">
              <div class="stat-card-num">{{ project.stats.rescinded }}</div>
              <div class="stat-card-label">已解约</div>
            </div>
          </div>

          <!-- 筛选行 -->
          <div class="filter-row">
            <el-date-picker
              v-model="project.dateRange"
              type="daterange"
              value-format="yyyy-MM-dd"
              range-separator="至"
              start-placeholder="签约开始日期"
              end-placeholder="签约结束日期"
              size="small"
              style="width: 250px;"
              :clearable="false"
              @change="onProjectFilterChange"
            ></el-date-picker>
            <el-select v-model="project.signType" placeholder="品牌类型" clearable size="small" style="width: 120px;" @change="onProjectFilterChange">
              <el-option v-for="item in signTypeOptions" :key="item" :label="item" :value="item"></el-option>
            </el-select>
            <el-select v-model="project.progress" placeholder="项目进度" clearable size="small" style="width: 110px;" @change="onProjectFilterChange">
              <el-option v-for="item in progressOptions" :key="item.value" :label="item.label" :value="item.value"></el-option>
            </el-select>
            <el-select v-model="project.signerId" placeholder="签约人" clearable size="small" style="width: 100px;" @change="onProjectFilterChange">
              <el-option v-for="item in signers" :key="item._id" :label="item.name" :value="item._id"></el-option>
            </el-select>
            <el-input v-model="project.keyword" placeholder="项目/客户" clearable size="small" style="width: 130px;" @keyup.enter.native="onProjectFilterChange" @clear="onProjectFilterChange"></el-input>
            <el-button size="small" @click="resetProjectFilter">重置</el-button>
          </div>

          <el-table v-loading="project.loading" :data="project.list" border stripe size="small" max-height="520">
            <el-table-column label="#" type="index" width="45" :index="i => (project.pagenum - 1) * project.pagesize + i + 1"></el-table-column>
            <el-table-column label="项目" prop="projectName" min-width="150" show-overflow-tooltip></el-table-column>
            <el-table-column label="客户" prop="customerName" min-width="100" show-overflow-tooltip></el-table-column>
            <el-table-column label="品牌" prop="signType" width="95" align="center"></el-table-column>
            <el-table-column label="签约人" prop="signerName" width="80" align="center"></el-table-column>
            <el-table-column label="签约日期" width="100" align="center">
              <template slot-scope="scope">{{ fmtDate(scope.row.dealDate) }}</template>
            </el-table-column>
            <el-table-column label="项目进度" width="85" align="center">
              <template slot-scope="scope">
                <el-tag :type="progressMap[scope.row.progress] && progressMap[scope.row.progress].type" size="mini">{{ progressMap[scope.row.progress] ? progressMap[scope.row.progress].text : '未知' }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="最新合同金额（元）" width="135" align="right">
              <template slot-scope="scope">{{ scope.row.contractType === null ? '—' : fmtMoney(scope.row.contractAmount) }}</template>
            </el-table-column>
            <el-table-column label="抽佣比例" width="85" align="center">
              <template slot-scope="scope">{{ scope.row.contractType !== null && scope.row.commissionRate ? scope.row.commissionRate + '%' : '—' }}</template>
            </el-table-column>
            <el-table-column label="服务截止日" width="100" align="center">
              <template slot-scope="scope">{{ scope.row.serviceEndDate || '—' }}</template>
            </el-table-column>
            <el-table-column label="合同类型" width="80" align="center">
              <template slot-scope="scope">
                <el-tag v-if="scope.row.contractType !== null" :type="scope.row.contractType === 2 ? '' : 'info'" size="mini">{{ scope.row.contractType === 2 ? '续签' : '新签' }}</el-tag>
                <span v-else class="muted-text">无合同</span>
              </template>
            </el-table-column>
            <el-table-column label="合同状态" width="85" align="center">
              <template slot-scope="scope">
                <el-tag v-if="scope.row.contractStatus !== null" :type="contractStatusMap[scope.row.contractStatus] && contractStatusMap[scope.row.contractStatus].type" size="mini">{{ contractStatusMap[scope.row.contractStatus] ? contractStatusMap[scope.row.contractStatus].text : '未知' }}</el-tag>
                <span v-else class="muted-text">—</span>
              </template>
            </el-table-column>
          </el-table>
          <el-pagination
            class="pager"
            @size-change="s => { project.pagesize = s; project.pagenum = 1; fetchProject() }"
            @current-change="p => { project.pagenum = p; fetchProject() }"
            :current-page="project.pagenum"
            :page-sizes="[10, 20, 50]"
            :page-size="project.pagesize"
            layout="total, sizes, prev, pager, next, jumper"
            :total="project.total">
          </el-pagination>
        </el-tab-pane>

        <!-- Tab3 人员绩效 -->
        <el-tab-pane label="人员绩效" name="perf">
          <!-- 指标卡：6 张 -->
          <div class="stat-cards">
            <div class="stat-card">
              <div class="stat-card-num">{{ perf.stats.signerCount }}</div>
              <div class="stat-card-label">参与人数</div>
            </div>
            <div class="stat-card">
              <div class="stat-card-num">{{ perf.stats.newCount }}</div>
              <div class="stat-card-label">新签合同数</div>
            </div>
            <div class="stat-card">
              <div class="stat-card-num">{{ perf.stats.renewCount }}</div>
              <div class="stat-card-label">续签合同数</div>
            </div>
            <div class="stat-card">
              <div class="stat-card-num">{{ fmtMoney(perf.stats.contractSum) }}</div>
              <div class="stat-card-label">合同总金额（元）</div>
            </div>
            <div class="stat-card">
              <div class="stat-card-num">{{ fmtMoney(perf.stats.incomeSum) }}</div>
              <div class="stat-card-label">实收金额（元）</div>
            </div>
            <div class="stat-card">
              <div class="stat-card-num">{{ fmtMoney(perf.stats.refundSum) }}</div>
              <div class="stat-card-label">退款金额（元）</div>
            </div>
          </div>

          <!-- 筛选行 -->
          <div class="filter-row">
            <el-date-picker
              v-model="perf.dateRange"
              type="daterange"
              value-format="yyyy-MM-dd"
              range-separator="至"
              start-placeholder="统计开始日期"
              end-placeholder="统计结束日期"
              size="small"
              style="width: 250px;"
              :clearable="false"
              @change="fetchPerf"
            ></el-date-picker>
            <el-radio-group v-model="perf.caliber" size="small" @change="fetchPerf">
              <el-radio-button label="sign">按签约日期</el-radio-button>
              <el-radio-button label="pay">按收款日期</el-radio-button>
            </el-radio-group>
            <el-select v-model="perf.department" placeholder="部门" clearable size="small" style="width: 130px;" @change="fetchPerf">
              <el-option v-for="item in departmentOptions" :key="item" :label="item" :value="item"></el-option>
            </el-select>
            <el-select v-model="perf.signerId" placeholder="签约人" clearable size="small" style="width: 100px;" @change="fetchPerf">
              <el-option v-for="item in signers" :key="item._id" :label="item.name" :value="item._id"></el-option>
            </el-select>
            <el-button size="small" @click="resetPerfFilter">重置</el-button>
            <span class="toolbar-tip" style="margin-left: auto;">口径：按签约日期→新签/续签落区间，实收/退款不受限；按收款日期→实收/退款落区间，新签/续签不受限</span>
          </div>

          <el-table v-loading="perf.loading" :data="perf.list" border stripe size="small">
            <el-table-column label="签约人" prop="signer" width="90" align="center"></el-table-column>
            <el-table-column label="部门" prop="department" min-width="130" align="center"></el-table-column>
            <el-table-column label="新签数" width="75" align="center">
              <template slot-scope="scope">{{ scope.row.newCount }}</template>
            </el-table-column>
            <el-table-column label="新签金额（元）" min-width="150" align="right">
              <template slot-scope="scope">{{ fmtMoney(scope.row.newAmount) }}</template>
            </el-table-column>
            <el-table-column label="续签数" width="75" align="center">
              <template slot-scope="scope">{{ scope.row.renewCount }}</template>
            </el-table-column>
            <el-table-column label="续签金额（元）" min-width="150" align="right">
              <template slot-scope="scope">{{ fmtMoney(scope.row.renewAmount) }}</template>
            </el-table-column>
            <el-table-column label="合同金额合计（元）" min-width="180" align="right">
              <template slot-scope="scope">{{ fmtMoney(scope.row.newAmount + scope.row.renewAmount) }}</template>
            </el-table-column>
            <el-table-column label="实收金额（元）" min-width="160" align="right">
              <template slot-scope="scope">{{ fmtMoney(scope.row.incomeAmount) }}</template>
            </el-table-column>
            <el-table-column label="退款金额（元）" min-width="150" align="right">
              <template slot-scope="scope">
                <span :class="scope.row.refundAmount > 0 ? 'money-refund' : ''">{{ fmtMoney(scope.row.refundAmount) }}</span>
              </template>
            </el-table-column>
            <el-table-column label="净收款（元）" min-width="160" align="right">
              <template slot-scope="scope">{{ fmtMoney(scope.row.incomeAmount - scope.row.refundAmount) }}</template>
            </el-table-column>
            <el-table-column label="待审核" width="75" align="center">
              <template slot-scope="scope">
                <span :class="scope.row.pendingCount > 0 ? 'pending-text' : ''">{{ scope.row.pendingCount }}</span>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>
      </el-tabs>
    </el-card>

    <!-- 图片预览（隐藏触发器：行内附件点击后调 clickHandler） -->
    <el-image
      v-show="false"
      ref="imageViewer"
      :src="currentImageUrl"
      :preview-src-list="imagePreviewList"
    ></el-image>

    <!-- PDF 预览对话框 -->
    <el-dialog :visible.sync="pdfPreviewDialogVisible" width="60%" :title="previewTitle" append-to-body>
      <div class="preview-container" style="height: 600px;">
        <iframe :src="previewUrl" style="width: 100%; height: 100%; border: none;"></iframe>
      </div>
    </el-dialog>

    <!-- 附件管理弹窗 -->
    <el-dialog title="附件管理" :visible.sync="attDialog.visible" width="720px" append-to-body @open="onAttDialogOpen" @close="onAttDialogClose">
      <div v-if="attDialog.row" class="att-dialog-summary">
        <span>客户：{{ attDialog.row.customerName || '—' }}</span>
        <span>项目：{{ attDialog.row.projectName || '无项目关联' }}</span>
        <span>金额：<span :class="attDialog.row.type === 1 ? 'money-text' : 'money-refund'">{{ fmtMoney(attDialog.row.totalAmount) }}</span> 元（{{ attDialog.row.type === 1 ? '收入' : '退款' }}）</span>
        <span>收款日期：{{ fmtDate(attDialog.row.payDate) }}</span>
      </div>
      <el-alert
        v-if="isAttReadonly"
        title="该记录已审核，不允许修改附件；如需调整请先取消审核"
        type="warning"
        :closable="false"
        show-icon
        style="margin-bottom: 12px;">
      </el-alert>
      <el-upload
        :action="uploadUrl"
        :headers="uploadHeaders"
        :on-success="handleAttUploadSuccess"
        :on-error="handleAttUploadError"
        :on-remove="handleAttRemove"
        :on-preview="handleAttPreview"
        :before-upload="beforeAttUpload"
        :file-list="attDialog.fileList"
        :disabled="isAttReadonly"
        accept=".jpg,.jpeg,.png,.gif,.bmp,.webp,.pdf"
        list-type="picture-card">
        <i class="el-icon-plus"></i>
        <div slot="tip" class="el-upload__tip">支持图片、PDF（不超过 50MB）；弹窗打开时可直接 Ctrl+V 粘贴图片</div>
      </el-upload>
      <div slot="footer">
        <el-button size="small" @click="attDialog.visible = false">取 消</el-button>
        <el-button v-if="!isAttReadonly" size="small" type="primary" :loading="attDialog.saving" @click="saveAttachments">保 存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { payTypeList, feeTypeList, payAccountList } from '@/utility/staticData'

export default {
  name: 'FinanceZhaoshangData',
  data () {
    return {
      activeTab: 'bill',
      // 枚举（复用全局静态字典，只读导入）
      payTypeList,
      feeTypeList,
      payAccountList,
      signTypeOptions: ['美滋滋', '蛮碗香', '长安大东家', '虎头张', '代运营', '其他'],
      progressOptions: [
        { value: 2, label: '开业中' },
        { value: 1, label: '筹建中' },
        { value: 0, label: '成交' },
        { value: -1, label: '退款' },
        { value: -2, label: '解约' }
      ],
      progressMap: {
        2: { text: '开业中', type: 'success' },
        1: { text: '筹建中', type: '' },
        0: { text: '成交', type: 'info' },
        '-1': { text: '退款', type: 'warning' },
        '-2': { text: '解约', type: 'danger' }
      },
      contractStatusMap: {
        0: { text: '生效中', type: 'success' },
        1: { text: '已到期', type: 'warning' },
        '-1': { text: '已解约', type: 'danger' }
      },

      // 签约人下拉（招商部门前缀在职员工）
      signers: [],

      // 附件预览（参照招商管理财务列表：行内图标直接预览，图片大图 / PDF iframe 对话框）
      base: '',
      currentImageUrl: '',
      imagePreviewList: [],
      pdfPreviewDialogVisible: false,
      previewUrl: '',
      previewTitle: '',
      pdfIconUrl: require('@/assets/pdf.png'),

      // 附件管理弹窗（上传配置 + 弹窗状态）
      uploadUrl: this.$http.defaults.baseURL + 'upload',
      uploadHeaders: {
        authorization: window.sessionStorage.getItem('token'),
        Accept: 'application/json'
      },
      attDialog: {
        visible: false,
        row: null,
        attachments: [], // [{ _uid, url, name, type }]
        fileList: [], // el-upload 展示列表（uid 与 attachments._uid 对应）
        saving: false,
        uploading: 0 // 上传中数量（点击上传 + 粘贴上传），保存前需归零
      },

      // 各 Tab 状态（服务端分页；筛选状态独立保留）
      bill: {
        dateRange: [],
        keyword: '',
        payType: '',
        feeType: '',
        payAccount: '',
        status: '',
        signerId: '',
        pagenum: 1,
        pagesize: 20,
        list: [],
        total: 0,
        loading: false,
        stats: { incomeCount: 0, incomeAmount: 0, refundCount: 0, refundAmount: 0, pendingCount: 0 }
      },
      project: {
        dateRange: [],
        signType: '',
        progress: '',
        signerId: '',
        keyword: '',
        pagenum: 1,
        pagesize: 20,
        list: [],
        total: 0,
        loading: false,
        stats: { total: 0, building: 0, opening: 0, rescinded: 0 }
      },
      perf: {
        dateRange: [],
        caliber: 'sign',
        department: '',
        signerId: '',
        list: [],
        loading: false,
        stats: { signerCount: 0, newCount: 0, renewCount: 0, contractSum: 0, incomeSum: 0, refundSum: 0 }
      }
    }
  },
  computed: {
    departmentOptions () {
      return [...new Set(this.signers.map(s => s.department))]
    },
    // 已审核记录附件只读（与招商域「已审核不允许修改」口径一致）
    isAttReadonly () {
      return !!(this.attDialog.row && this.attDialog.row.status === 1)
    }
  },
  created () {
    // 三个 Tab 查询时间默认当月（本月第一天 ~ 今天）
    const defaultRange = this._defaultMonthRange()
    this.bill.dateRange = defaultRange
    this.project.dateRange = [...defaultRange]
    this.perf.dateRange = [...defaultRange]
    this.base = window.sessionStorage.getItem('base') || ''
    this.loadOptions()
    this.fetchBill()
    this.fetchProject()
    this.fetchPerf()
  },
  beforeDestroy () {
    document.removeEventListener('paste', this.handlePasteUpload)
  },
  methods: {
    /**
     * 默认查询区间：本月第一天 ~ 今天（YYYY-MM-DD）
     */
    _defaultMonthRange () {
      const fmtDay = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      const now = new Date()
      return [fmtDay(new Date(now.getFullYear(), now.getMonth(), 1)), fmtDay(now)]
    },
    /**
     * 金额格式化：两位小数 + 千分位
     */
    fmtMoney (v) {
      if (v === null || v === undefined || isNaN(v)) return '—'
      return Number(v).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },

    /**
     * 日期格式化（东八区，YYYY-MM-DD）：后端返回 ISO 字符串，勿直接截取前 10 位（UTC 会差 8 小时）
     */
    fmtDate (v) {
      return v ? new Date(v).toLocaleDateString('sv-SE') : ''
    },

    /**
     * 筛选日期段拼文件名用
     */
    _dateTag (range) {
      const [start, end] = range || []
      if (start && end) return start === end ? String(start) : `${start}_${end}`
      return '全部'
    },

    /**
     * 组装收款流水筛选参数（列表与导出共用，保证所见即所得）
     */
    _buildBillParams () {
      const params = {
        pagenum: this.bill.pagenum,
        pagesize: this.bill.pagesize
      }
      const [startDate, endDate] = this.bill.dateRange || []
      if (startDate) params.startDate = startDate
      if (endDate) params.endDate = endDate
      if (this.bill.keyword.trim()) params.keyword = this.bill.keyword.trim()
      if (this.bill.payType) params.payType = this.bill.payType
      if (this.bill.feeType) params.feeType = this.bill.feeType
      if (this.bill.payAccount) params.payAccount = this.bill.payAccount
      if (this.bill.status !== '' && this.bill.status !== null) params.status = this.bill.status
      if (this.bill.signerId) params.signerId = this.bill.signerId
      return params
    },

    /**
     * 签约人下拉选项
     */
    async loadOptions () {
      try {
        const { data: res } = await this.$http.get('finance/zhaoshangData/filter/options')
        if (res.meta.status !== 200) return this.$message.error(res.meta.msg)
        this.signers = res.data.signers || []
      } catch (error) {
        this.$message.error('获取签约人列表失败')
      }
    },

    /* ===== Tab1 收款流水 ===== */
    onBillFilterChange () {
      // 筛选变更回到第一页，避免旧页码超出新结果集导致空列表
      this.bill.pagenum = 1
      this.fetchBill()
    },
    async fetchBill () {
      this.bill.loading = true
      try {
        const { data: res } = await this.$http.get('finance/zhaoshangData/bill/list', { params: this._buildBillParams() })
        if (res.meta.status !== 200) return this.$message.error(res.meta.msg)
        this.bill.list = res.data.list || []
        this.bill.total = res.data.total || 0
        this.bill.stats = res.data.stats || this.bill.stats
      } catch (error) {
        this.$message.error('获取收款流水失败')
      } finally {
        this.bill.loading = false
      }
    },
    resetBillFilter () {
      this.bill = {
        dateRange: this._defaultMonthRange(),
        keyword: '',
        payType: '',
        feeType: '',
        payAccount: '',
        status: '',
        signerId: '',
        pagenum: 1,
        pagesize: 20,
        list: [],
        total: 0,
        loading: false,
        stats: { incomeCount: 0, incomeAmount: 0, refundCount: 0, refundAmount: 0, pendingCount: 0 }
      }
      this.fetchBill()
    },
    toggleAudit (row) {
      const target = row.status === 1 ? 0 : 1
      this.$confirm(`确认${target === 1 ? '审核通过' : '取消审核'}该笔记录？`, '提示', { type: 'warning' })
        .then(async () => {
          try {
            const { data: res } = await this.$http.put(`finance/zhaoshangData/bill/audit/${row._id}`, { status: target })
            if (res.meta.status !== 200) return this.$message.error(res.meta.msg)
            this.$message.success(target === 1 ? '已审核' : '已取消审核')
            this.fetchBill()
          } catch (error) {
            this.$message.error('审核操作失败')
          }
        })
        .catch(() => {})
    },
    getFileUrl (url) {
      if (!url) return ''
      return this.base + url
    },
    _guessFileType (name) {
      const ext = String(name || '').split('.').pop().toLowerCase()
      if (['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp'].includes(ext)) return 'image'
      return 'pdf'
    },
    handlePreviewAttachment (row, file) {
      if (!file) return
      const fileUrl = this.getFileUrl(file.url)
      const type = file.type || this._guessFileType(file.name)
      this.previewTitle = file.name || '附件预览'
      if (type === 'image') {
        // 构建当前行图片列表并从点击图开始（多图可切换）
        const imgs = (row.attachments || []).filter(a => (a.type || this._guessFileType(a.name)) === 'image')
        const i = Math.max(0, imgs.indexOf(file))
        const urls = imgs.map(a => this.getFileUrl(a.url))
        this.imagePreviewList = [...urls.slice(i), ...urls.slice(0, i)]
        this.currentImageUrl = fileUrl
        this.$nextTick(() => {
          this.$refs.imageViewer.clickHandler()
        })
      } else if (type === 'pdf') {
        this.previewUrl = fileUrl
        this.pdfPreviewDialogVisible = true
      } else {
        this.$message.info('不支持预览该文件类型')
      }
    },

    /* ===== 附件管理弹窗 ===== */
    /**
     * 上传类型判定：image / pdf，其他返回 null
     */
    _guessUploadType (name) {
      const ext = String(name || '').split('.').pop().toLowerCase()
      if (['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp'].includes(ext)) return 'image'
      if (ext === 'pdf') return 'pdf'
      return null
    },
    openAttDialog (row) {
      this.attDialog.row = row
      const stamp = Date.now()
      this.attDialog.attachments = (row.attachments || []).map((item, index) => ({
        _uid: `att_${stamp}_${index}`,
        url: item.url,
        name: item.name,
        type: item.type || this._guessFileType(item.name)
      }))
      this.attDialog.fileList = this.attDialog.attachments.map(item => {
        const type = item.type || this._guessFileType(item.name)
        return {
          name: item.name,
          url: type === 'pdf' ? this.pdfIconUrl : this.getFileUrl(item.url),
          uid: item._uid,
          status: 'success'
        }
      })
      this.attDialog.saving = false
      this.attDialog.uploading = 0
      this.attDialog.visible = true
    },
    onAttDialogOpen () {
      document.addEventListener('paste', this.handlePasteUpload)
    },
    onAttDialogClose () {
      document.removeEventListener('paste', this.handlePasteUpload)
      this.attDialog.row = null
      this.attDialog.attachments = []
      this.attDialog.fileList = []
    },
    beforeAttUpload (file) {
      const type = this._guessUploadType(file.name)
      if (!type) {
        this.$message.error('只支持上传图片或PDF文件')
        return false
      }
      if (file.size / 1024 / 1024 >= 50) {
        this.$message.error('文件大小不能超过 50MB')
        return false
      }
      this.attDialog.uploading++
      return true
    },
    handleAttUploadSuccess (res, file, fileList) {
      this.attDialog.uploading--
      if (res.meta.status !== 200) {
        const idx = fileList.findIndex(f => f.uid === file.uid)
        if (idx !== -1) fileList.splice(idx, 1)
        this.attDialog.fileList = fileList
        return this.$message.error(res.meta.msg || '上传失败')
      }
      const type = this._guessUploadType(file.name) || 'other'
      if (type === 'pdf') {
        // 列表显示统一 PDF 图标，预览时经 attachments 取实际地址
        const item = fileList.find(f => f.uid === file.uid)
        if (item) item.url = this.pdfIconUrl
      }
      this.attDialog.attachments.push({ _uid: file.uid, url: res.data.url, name: file.name, type })
      this.attDialog.fileList = fileList
    },
    handleAttUploadError (err, file, fileList) {
      console.error('附件上传失败:', err)
      this.attDialog.uploading--
      const idx = fileList.findIndex(f => f.uid === file.uid)
      if (idx !== -1) fileList.splice(idx, 1)
      this.attDialog.fileList = fileList
      this.$message.error('上传失败，请重试')
    },
    handleAttRemove (file, fileList) {
      const idx = this.attDialog.attachments.findIndex(a => a._uid === file.uid)
      if (idx !== -1) this.attDialog.attachments.splice(idx, 1)
      this.attDialog.fileList = fileList
    },
    handleAttPreview (file) {
      const attachment = this.attDialog.attachments.find(a => a._uid === file.uid)
      if (!attachment) return
      const fileUrl = this.getFileUrl(attachment.url)
      this.previewTitle = attachment.name || '附件预览'
      if (attachment.type === 'image') {
        const imgs = this.attDialog.attachments.filter(a => a.type === 'image')
        const i = Math.max(0, imgs.indexOf(attachment))
        const urls = imgs.map(a => this.getFileUrl(a.url))
        this.imagePreviewList = [...urls.slice(i), ...urls.slice(0, i)]
        this.currentImageUrl = fileUrl
        this.$nextTick(() => {
          this.$refs.imageViewer.clickHandler()
        })
      } else if (attachment.type === 'pdf') {
        this.previewUrl = fileUrl
        this.pdfPreviewDialogVisible = true
      } else {
        this.$message.info('不支持预览该文件类型')
      }
    },
    /**
     * 弹窗打开期间监听全局粘贴：剪贴板中的图片直接上传
     */
    handlePasteUpload (e) {
      if (!this.attDialog.visible || this.isAttReadonly) return
      const items = e.clipboardData && e.clipboardData.items
      if (!items) return
      for (let i = 0; i < items.length; i++) {
        if (items[i].type && items[i].type.startsWith('image/')) {
          const file = items[i].getAsFile()
          if (file) this.uploadPasteImage(file)
        }
      }
    },
    async uploadPasteImage (file) {
      const now = new Date()
      const p = n => String(n).padStart(2, '0')
      const name = `粘贴图片_${now.getFullYear()}${p(now.getMonth() + 1)}${p(now.getDate())}${p(now.getHours())}${p(now.getMinutes())}${p(now.getSeconds())}.png`
      const uid = `paste_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
      this.attDialog.uploading++
      // 先用本地预览占位，上传完成后替换为服务器地址
      const previewUrl = await new Promise(resolve => {
        const reader = new FileReader()
        reader.onload = ev => resolve(ev.target.result)
        reader.readAsDataURL(file)
      })
      this.attDialog.fileList.push({ name, url: previewUrl, uid, status: 'uploading', percentage: 0 })
      try {
        const formData = new FormData()
        formData.append('file', file, name)
        const { data: res } = await this.$http.post('upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data', Accept: 'application/json' }
        })
        if (res.meta.status !== 200) {
          this._removeAttFileByUid(uid)
          return this.$message.error(res.meta.msg || '粘贴上传失败')
        }
        this.attDialog.attachments.push({ _uid: uid, url: res.data.url, name, type: 'image' })
        const idx = this.attDialog.fileList.findIndex(f => f.uid === uid)
        if (idx !== -1) {
          this.attDialog.fileList.splice(idx, 1, { name, url: this.getFileUrl(res.data.url), uid, status: 'success' })
        }
        this.$message.success('粘贴上传成功')
      } catch (error) {
        this._removeAttFileByUid(uid)
        this.$message.error('粘贴上传失败')
      } finally {
        this.attDialog.uploading--
      }
    },
    _removeAttFileByUid (uid) {
      const idx = this.attDialog.fileList.findIndex(f => f.uid === uid)
      if (idx !== -1) this.attDialog.fileList.splice(idx, 1)
    },
    async saveAttachments () {
      if (this.attDialog.uploading > 0) return this.$message.warning('附件仍在上传中，请稍候再保存')
      const attachments = this.attDialog.attachments.map(a => ({ url: a.url, name: a.name, type: a.type }))
      this.attDialog.saving = true
      try {
        const { data: res } = await this.$http.put(`finance/zhaoshangData/bill/attachments/${this.attDialog.row._id}`, { attachments })
        if (res.meta.status !== 200) return this.$message.error(res.meta.msg)
        this.$message.success('附件已保存')
        // 原地更新行数据，附件列缩略图即时刷新，避免整表重载闪烁
        this.attDialog.row.attachments = attachments
        this.attDialog.visible = false
      } catch (error) {
        this.$message.error('保存附件失败')
      } finally {
        this.attDialog.saving = false
      }
    },
    async handleBillExport () {
      if (!this.bill.total) return this.$message.warning('当前筛选条件下无数据可导出')
      try {
        const { data } = await this.$http.get('finance/zhaoshangData/bill/export', { params: this._buildBillParams(), responseType: 'blob' })
        if (data.type === 'application/json') {
          const err = JSON.parse(await data.text())
          return this.$message.error((err.meta && err.meta.msg) || '导出失败')
        }
        this._downloadBlob(data, `招商收款流水_${this._dateTag(this.bill.dateRange)}.xlsx`)
      } catch (error) {
        this.$message.error('导出失败')
      }
    },
    _downloadBlob (blob, filename) {
      const url = window.URL.createObjectURL(new Blob([blob]))
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    },

    /* ===== Tab2 签约统计 ===== */
    onProjectFilterChange () {
      this.project.pagenum = 1
      this.fetchProject()
    },
    async fetchProject () {
      this.project.loading = true
      try {
        const params = {
          pagenum: this.project.pagenum,
          pagesize: this.project.pagesize
        }
        const [startDate, endDate] = this.project.dateRange || []
        if (startDate) params.startDate = startDate
        if (endDate) params.endDate = endDate
        if (this.project.signType) params.signType = this.project.signType
        if (this.project.progress !== '' && this.project.progress !== null) params.progress = this.project.progress
        if (this.project.signerId) params.signerId = this.project.signerId
        if (this.project.keyword.trim()) params.keyword = this.project.keyword.trim()
        const { data: res } = await this.$http.get('finance/zhaoshangData/project/list', { params })
        if (res.meta.status !== 200) return this.$message.error(res.meta.msg)
        this.project.list = res.data.list || []
        this.project.total = res.data.total || 0
        this.project.stats = res.data.stats || this.project.stats
      } catch (error) {
        this.$message.error('获取签约统计失败')
      } finally {
        this.project.loading = false
      }
    },
    resetProjectFilter () {
      this.project = {
        dateRange: this._defaultMonthRange(),
        signType: '',
        progress: '',
        signerId: '',
        keyword: '',
        pagenum: 1,
        pagesize: 20,
        list: [],
        total: 0,
        loading: false,
        stats: { total: 0, building: 0, opening: 0, rescinded: 0 }
      }
      this.fetchProject()
    },

    /* ===== Tab3 人员绩效 ===== */
    async fetchPerf () {
      this.perf.loading = true
      try {
        const params = { caliber: this.perf.caliber }
        const [startDate, endDate] = this.perf.dateRange || []
        if (startDate) params.startDate = startDate
        if (endDate) params.endDate = endDate
        if (this.perf.department) params.department = this.perf.department
        if (this.perf.signerId) params.signerId = this.perf.signerId
        const { data: res } = await this.$http.get('finance/zhaoshangData/employee/stats', { params })
        if (res.meta.status !== 200) return this.$message.error(res.meta.msg)
        this.perf.list = res.data.list || []
        this.perf.stats = res.data.stats || this.perf.stats
      } catch (error) {
        this.$message.error('获取人员绩效失败')
      } finally {
        this.perf.loading = false
      }
    },
    resetPerfFilter () {
      this.perf = {
        dateRange: this._defaultMonthRange(),
        caliber: 'sign',
        department: '',
        signerId: '',
        list: [],
        loading: false,
        stats: { signerCount: 0, newCount: 0, renewCount: 0, contractSum: 0, incomeSum: 0, refundSum: 0 }
      }
      this.fetchPerf()
    }
  }
}
</script>

<style lang="less" scoped>
.container {
  .main-card {
    margin-top: 15px;
  }
}

.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.card-header {
  font-size: 16px;
  font-weight: bold;
}
.header-summary {
  font-size: 12px;
  font-weight: normal;
  color: #909399;
  margin-left: 12px;
}
.toolbar-tip {
  color: #909399;
  font-size: 12px;
}

/* 指标卡 */
.stat-cards {
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
}
.stat-card {
  flex: 1;
  min-width: 0;
  padding: 10px 12px;
  border: 1px solid #EBEEF5;
  border-radius: 4px;
  background: #FAFBFC;
  text-align: center;
}
.stat-card-num {
  font-size: 24px;
  font-weight: bold;
  line-height: 1.2;
  color: #303133;
}
.stat-card.warn .stat-card-num {
  color: #F56C6C;
}
.stat-card-label {
  font-size: 12px;
  color: #606266;
  margin-top: 2px;
}

/* 筛选行 */
.filter-row {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  padding-top: 4px;
  flex-wrap: wrap;
  row-gap: 8px;
  .el-select,
  .el-input,
  .el-date-editor {
    margin-right: 8px;
  }
  .el-button {
    margin-left: 0;
    margin-right: 8px;
  }
}

/* 表格内小字 */
.project-name {
  font-size: 13px;
  color: #303133;
}
.muted-text {
  color: #c0c4cc;
}
.money-text {
  color: #303133;
  font-weight: 500;
}
.money-refund {
  color: #F56C6C;
  font-weight: 500;
}
.pending-text {
  color: #E6A23C;
  font-weight: 500;
}
.unassigned-text {
  color: #F56C6C;
  font-size: 13px;
  font-weight: 500;
}

.pager {
  margin-top: 12px;
  text-align: right;
}

/* 附件行内展示（参照招商管理财务列表） */
.att-cell {
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
}
.att-item-btn {
  cursor: pointer;
  line-height: 1;
}
.att-thumb-sm {
  width: 20px;
  height: 20px;
  object-fit: cover;
  border-radius: 2px;
  border: 1px solid #dcdfe6;

  &.is-pdf {
    object-fit: contain;
  }
}
.att-doc-icon {
  font-size: 20px;
  color: #909399;
}

/* 附件管理弹窗摘要 */
.att-dialog-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 24px;
  font-size: 13px;
  color: #606266;
  margin-bottom: 12px;
}

// 图片预览层级高于页面弹窗
::v-deep .el-image-viewer__wrapper {
  z-index: 3000;
}
</style>
